import React from 'react';
import axios from 'axios';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import '@fontsource-variable/inter';
import App from './app';
import { Toaster } from '@/components/ui/toaster';
import { ThemeProvider } from '@/components/ThemeProvider';
import './index.css';

import AddCompany from '@/pages/companies/Add';
import Company from '@/pages/companies/Company';
import EditCompany from '@/pages/companies/Edit';
import ListCompanies from '@/pages/companies/List';
import LoginPage from '@/pages/login/Page';
import MsaPremiumDefaultsPage from '@/pages/msaPremiumDefaults/Page';
import Users from '@/pages/companies/Users';
import Integrations from '@/pages/companies/Integrations';
import Domains from '@/pages/companies/Domains';
import ListDivisions from '@/pages/divisions/ListDivisions';
import DashboardPage from '@/pages/dashboard/Page';
import ListSectorTypePage from '@/pages/sectorType/ListSectorTypePage';
import AddSectorTypePage from '@/pages/sectorType/AddSectorTypePage';
import ViewSectorTypePage from '@/pages/sectorType/ViewSectorTypePage';
import EditSectorType from '@/pages/sectorType/EditSectorType';
import ListSectorSubTypes from '@/pages/sectorSubType/ListSectorSubTypes';
import ListMarketDesignFields from '@/pages/marketDesignField/ListMarketDesignFields';
import ListGlobalCurrencies from '@/pages/globalCurrencies/ListGlobalCurrencies';
import FeatureUsagePage from '@/pages/featureUsage/Page';
import RequestAnalyticsPage from '@/pages/requestAnalytics/Page';
import CleanupCostDataPage from '@/pages/cleanupCostData/Page';
import LlmUsagePage from '@/pages/llmUsage/Page';
import ListAiAgents from '@/pages/aiAgents/ListAiAgents';
import AiAgents from '@/pages/companies/AiAgents';
import VolumeTab from '@/pages/requestAnalytics/VolumeTab';
import CompanyActivityTab from '@/pages/requestAnalytics/CompanyActivityTab';
import SlowestRoutesTab from '@/pages/requestAnalytics/SlowestRoutesTab';
import ErrorsTab from '@/pages/requestAnalytics/ErrorsTab';
import TasksPage from '@/pages/tasks/Page';
import TaskList from '@/pages/tasks/TaskList';
import FeatureIdeas from '@/pages/tasks/FeatureIdeas';
import BugReports from '@/pages/tasks/BugReports';
import ToolsPage from '@/pages/tools/Page';

import { getAuth0Props } from '@/helpers/auth0';
import { getApiUrl } from '@/helpers/api';

axios.defaults.baseURL = getApiUrl();

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />
  },
  {
    path: '/',
    element: <App />,
    children: [
      {
        index: true,
        element: <DashboardPage />
      },
      {
        path: 'company',
        children: [
          {
            index: true,
            element: <ListCompanies />
          },
          {
            path: ':companyUid',
            element: <Company />,
            children: [
              {
                index: true,
                element: <EditCompany />
              },
              {
                path: 'users',
                element: <Users />
              },
              {
                path: 'integrations',
                element: <Integrations />
              },
              {
                path: 'domains',
                element: <Domains />
              },
              {
                path: 'ai-agents',
                element: <AiAgents />
              }
            ]
          },
          {
            path: 'add',
            element: <AddCompany />
          }
        ]
      },
      {
        path: 'feature-usage',
        element: <FeatureUsagePage />
      },
      {
        path: 'msa-premium-defaults',
        element: <MsaPremiumDefaultsPage />
      },
      {
        path: 'global-currencies',
        element: <ListGlobalCurrencies />
      },
      {
        path: 'request-analytics',
        element: <RequestAnalyticsPage />,
        children: [
          { index: true, element: <VolumeTab /> },
          { path: 'company-activity', element: <CompanyActivityTab /> },
          { path: 'slowest-routes', element: <SlowestRoutesTab /> },
          { path: 'errors', element: <ErrorsTab /> }
        ]
      },
      {
        path: 'cleanup-cost-data',
        element: <CleanupCostDataPage />
      },
      {
        path: 'llm-usage',
        element: <LlmUsagePage />
      },
      {
        path: 'ai-agents',
        element: <ListAiAgents />
      },
      {
        path: 'tasks',
        element: <TasksPage />,
        children: [
          { index: true, element: <TaskList /> },
          { path: 'ideas', element: <FeatureIdeas /> },
          { path: 'bugs', element: <BugReports /> }
        ]
      },
      {
        path: 'tools',
        element: <ToolsPage />
      },
      {
        path: 'sector-type',
        children: [
          { index: true, element: <ListSectorTypePage /> },
          {
            path: ':sectorTypeUid',
            element: <ViewSectorTypePage />,
            children: [
              { index: true, element: <EditSectorType /> },
              { path: 'sector-sub-type', element: <ListSectorSubTypes /> },
              { path: 'division', element: <ListDivisions /> },
              { path: 'market-design-field', element: <ListMarketDesignFields /> }
            ]
          },
          { path: 'add', element: <AddSectorTypePage /> }
        ]
      }
    ]
  }
]);

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <Auth0Provider {...getAuth0Props()}>
    <ThemeProvider>
      <RouterProvider router={router} />
      <Toaster />
    </ThemeProvider>
  </Auth0Provider>
);
